import type { QuarterlyGoal, Snapshot } from '../lib/snapshot-types.js';
import { etiquetaTrimestre } from '../lib/etiquetas-metricas.js';

const LABELS: Record<QuarterlyGoal['metric'], string> = {
  revenue: 'Facturación', meetings: 'Reuniones celebradas', calls_connected: 'Llamadas conectadas',
  opportunities_won: 'Oportunidades ganadas', sequences_enrolled: 'Altas en secuencias',
};
const REASONS: Record<string, string> = {
  calls_module_disabled: 'La medición de llamadas no está activada.',
  calls_window_not_quarter: 'El rango seleccionado no cubre el trimestre hasta hoy.',
  scope_measurement_unavailable: 'No hay medición disponible para este alcance.',
  meetings_source_unavailable: 'La fuente de reuniones no está disponible.',
  revenue_currency_unknown: 'No se conoce la moneda del importe medido.',
  revenue_source_unavailable: 'La fuente de facturación no está disponible.',
  opportunities_measurement_unavailable: 'No hay medición de este objetivo disponible.',
  sequences_measurement_unavailable: 'No hay medición de contactos inscritos disponible.',
  meetings_window_not_quarter: 'El periodo elegido no es el trimestre hasta hoy.',
};

/**
 * Los objetivos que se ven. En un snapshot 2.0 (con `filtro`) el servidor ya los
 * filtra por las personas elegidas y mide cada uno solo si el alcance coincide:
 * aqui no se vuelve a filtrar. En uno de antes, "me" deja los propios.
 */
export function goalsForView(snapshot: Snapshot, scope: 'me' | 'team', email: string): QuarterlyGoal[] {
  const delTrimestre = snapshot.quarterly_goals?.filter((g) => (
    g.year === snapshot.quarter.year && g.quarter === snapshot.quarter.q
    && Object.prototype.hasOwnProperty.call(LABELS, g.metric)
    && (!g.user_email || Object.prototype.hasOwnProperty.call(snapshot.reps, g.user_email))
  )) ?? [];
  if (snapshot.filtro) return delTrimestre;
  return delTrimestre.filter((g) => !g.user_email || scope === 'team' || g.user_email === email);
}

/** El nombre de quien tiene el objetivo (nunca su email si se sabe el nombre); "Empresa" si es de todos. */
function alcanceDe(snapshot: Snapshot, email: string | null): string {
  if (!email) return 'Empresa';
  return snapshot.reps[email]?.name?.trim() || email;
}

export function QuarterlyGoals({ snapshot, scope, email }: { snapshot: Snapshot; scope: 'me' | 'team'; email: string }) {
  const goals = goalsForView(snapshot, scope, email);
  if (!goals.length) return null;
  const trimestre = etiquetaTrimestre(snapshot.quarter.label);
  return (
    <section className="section" aria-label={`Objetivos ${trimestre}`}>
      <h2 className="section-title">Objetivos {trimestre} <span className="seccion-periodo">· el trimestre entero</span></h2>
      <div style={{ overflowX: 'auto' }}>
      <table className="table">
        <thead><tr><th>Métrica</th><th>Alcance</th><th>Objetivo</th><th>Conseguido</th><th>Progreso</th></tr></thead>
        <tbody>{goals.map((g) => {
          // En un snapshot de antes, "Yo" no puede leer el conseguido de la empresa.
          const personalOrg = !snapshot.filtro && scope === 'me' && g.user_email === null;
          const achieved = personalOrg ? null : g.achieved;
          const reason = personalOrg ? 'scope_measurement_unavailable' : g.reason;
          return (
          <tr key={`${g.metric}:${g.user_email ?? 'empresa'}`}>
            <td>{LABELS[g.metric]}</td>
            <td>{alcanceDe(snapshot, g.user_email)}</td>
            <td>{g.target}{g.metric === 'revenue' ? ` ${g.currency ?? '(moneda sin declarar)'}` : ''}</td>
            {achieved === null ? <>
              <td colSpan={2}>Sin medición <span style={{ color: 'var(--fg-subtle)', fontSize: 11 }}>{REASONS[reason ?? ''] ?? 'No hay medición disponible.'}</span></td>
            </> : <>
              <td>{achieved}{g.metric === 'revenue' ? ` ${g.currency ?? ''}` : ''}</td>
              <td>{g.progress_pct === null ? '-' : `${Math.round(g.progress_pct * 100)}%`}</td>
            </>}
          </tr>
        ); })}</tbody>
      </table>
      </div>
    </section>
  );
}
